import { useEffect, useMemo, useState } from 'react';
import {
  Achado, Marco, Plano, Reta, Serie, Zoom, br, maisPerto, useJanela,
  type Janela, type Ponto,
} from '../Grafico';
import type { CorpoDeProvaCd, EnsaioCisalhamentoDireto } from '../api/cisalhamentoDireto';

/**
 * Os gráficos do cisalhamento direto — na etapa de INTERPRETAÇÃO (265).
 *
 * «Os gráficos de ensaio devem ser apresentados na parte de
 * interpretação» — Humberto, 03/10/2026. São as curvas do laudo, com as
 * contas prontas do banco (`vw_cisalhamento_direto_*`): a tela não tira
 * raiz, não divide força por área e não ajusta reta (D-29). Até a
 * abscissa √t vem pronta (`raiz_tempo_min`, `raiz_t90_min`…).
 *
 * O gráfico não decide nada: clicar SELECIONA a leitura mais próxima, e
 * o que se faz com ela é um botão.
 */

const num = (v: unknown): number | null => {
  if (v === null || v === undefined || v === '') return null;
  const x = Number(v);
  return Number.isFinite(x) ? x : null;
};

/** Rótulo curto de cada critério, para caber em cima da curva. */
export const SIGLA_CRITERIO_CD: Record<string, string> = {
  max_cisalhante: 'τ máx',
  deslocamento_relativo_10: '10 % δh/L',
  deslocamento_limite: 'δh limite',
  manual: 'apontado',
};

/** Pontos sem os nulos — a curva não inventa leitura que não existe. */
function serie(linhas: any[], fx: (l: any) => unknown, fy: (l: any) => unknown): Ponto[] {
  const s: Ponto[] = [];
  for (const l of linhas) {
    const x = num(fx(l)); const y = num(fy(l));
    if (x !== null && y !== null) s.push({ x, y });
  }
  return s;
}

/** Janela que contém o zero dos dois eixos. */
function janelaDoZero(pts: Ponto[], folga = 0.08): Janela {
  if (!pts.length) return { x0: 0, x1: 1, y0: 0, y1: 1 };
  const xs = pts.map((p) => p.x); const ys = pts.map((p) => p.y);
  const x0 = Math.min(0, ...xs); const x1 = Math.max(...xs);
  const y0 = Math.min(0, ...ys); const y1 = Math.max(...ys);
  const fx = (x1 - x0) * folga || 1; const fy = (y1 - y0) * folga || 1;
  return { x0: x0 < 0 ? x0 - fx : 0, x1: x1 + fx, y0: y0 < 0 ? y0 - fy : 0, y1: y1 + fy };
}

/** A distância do clique medida NA TELA (frações da janela). */
const naTela = (j: Janela) => ({
  x: { para: (v: number) => (v - j.x0) / (j.x1 - j.x0) },
  y: { para: (v: number) => (v - j.y0) / (j.y1 - j.y0) },
});

const classeCp = (k: number) => `trajetoria cp-${(k % 6) + 1}`;

/**
 * As leituras a partir do assentamento imediato — o maior degrau no
 * primeiro quinto das leituras, quando ele é mais da metade do
 * deslocamento todo. É ENQUADRAMENTO, e a mesma regra do laudo
 * (`semAssentamento` em `cisalhamento-direto-documento.service.ts`).
 */
function semAssentamento(pts: Ponto[]): Ponto[] {
  if (pts.length < 6) return pts;
  const ys = pts.map((p) => p.y);
  const faixa = Math.max(...ys) - Math.min(...ys);
  let k = -1; let maior = 0;
  for (let i = 1; i <= Math.ceil(pts.length / 5); i += 1) {
    const d = Math.abs(pts[i].y - pts[i - 1].y);
    if (d > maior) { maior = d; k = i; }
  }
  if (k < 1 || faixa <= 0 || maior < 0.5 * faixa) return pts;
  return pts.slice(k);
}

// ══ O traçado do tempo até a falha ════════════════════════════════════

/**
 * δv × √t do adensamento de UM corpo de prova, com a construção que dá o
 * tempo até a falha: dois pontos do trecho inicial (A e B) e o fim do
 * adensamento primário. Com eles o banco apura t₉₀ por Taylor (ASTM
 * D3080 9.10.2: a reta de abscissa 1,15 vez a da primeira) e t₁₀₀ pela
 * BS 1377 (a reta cortada pela horizontal do fim do primário).
 *
 * Quem interpreta clica na curva, escolhe o que a leitura é, e grava — o
 * traçado é dele, e o laudo diz isso.
 */
export function GraficoDoTracado({ cp, aoGravar, gravando }: {
  cp: CorpoDeProvaCd;
  aoGravar(t: { pontoA: number; pontoB: number; pontoFim: number | null }): void;
  gravando: boolean;
}) {
  const leituras = useMemo(
    () => (cp.adensamento ?? []).filter((a: any) => !a.descartada
      && num(a.raiz_tempo_min) !== null && num(a.deslocamento_vertical_mm) !== null),
    [cp.adensamento]);
  const pts = useMemo(
    () => serie(leituras, (a) => a.raiz_tempo_min, (a) => a.deslocamento_vertical_mm),
    [leituras]);
  /* O enquadramento parte do fim do assentamento imediato (−3 mm nos
     primeiros 15 s, na CA-04), que achataria o trecho em que a reta se
     traça. As leituras de antes continuam lá — o zoom as mostra. */
  const enquadrar = useMemo(() => semAssentamento(pts), [pts]);
  const z = useJanela(enquadrar, 0.06);
  const v = cp.velocidade;
  const salvo = {
    a: num(v?.ponto_a_ordem), b: num(v?.ponto_b_ordem), fim: num(v?.ponto_fim_ordem),
  };
  const [rasc, setRasc] = useState(salvo);
  const [sel, setSel] = useState<number | null>(null);
  useEffect(() => {
    setRasc({ a: num(v?.ponto_a_ordem), b: num(v?.ponto_b_ordem), fim: num(v?.ponto_fim_ordem) });
    setSel(null);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cp.cp.id, v?.ponto_a_ordem, v?.ponto_b_ordem, v?.ponto_fim_ordem]);

  if (pts.length < 2) return null;

  const daOrdem = (o: number | null) => (o === null ? null
    : leituras.find((l: any) => Number(l.ordem) === o) ?? null);
  const ponto = (l: any | null): Ponto | null => (l
    ? { x: Number(l.raiz_tempo_min), y: Number(l.deslocamento_vertical_mm) } : null);
  const a = num(v?.reta_intercepto);
  const b = num(v?.reta_inclinacao);
  const yFim = num(v?.deslocamento_vertical_fim_mm);
  const p90 = num(v?.raiz_t90_min) !== null && num(v?.deslocamento_vertical_t90_mm) !== null
    ? { x: Number(v.raiz_t90_min), y: Number(v.deslocamento_vertical_t90_mm) } : null;
  const p100 = num(v?.raiz_t100_min) !== null && yFim !== null
    ? { x: Number(v.raiz_t100_min), y: yFim } : null;
  const lSel = sel !== null ? leituras[sel] : null;
  const mudou = rasc.a !== salvo.a || rasc.b !== salvo.b || rasc.fim !== salvo.fim;
  const marca = (k: 'a' | 'b' | 'fim') => () => {
    if (!lSel) return;
    setRasc((r) => ({ ...r, [k]: Number(lSel.ordem) }));
  };
  const descreve = (o: number | null) => {
    const l = daOrdem(o);
    return l ? `leitura ${l.ordem} · t = ${br(Number(l.tempo_min), 2)} min · δv = ${
      br(Number(l.deslocamento_vertical_mm), 3)} mm` : '—';
  };

  return (
    <figure className="grafico grafico-triaxial" data-grafico="tracado">
      <figcaption>
        Deslocamento vertical × √t — {cp.cp.identificacao}
      </figcaption>
      <Zoom z={z} />
      <Plano janela={z.janela} aoJanela={z.definir}
        tituloX="√t (min^½)" tituloY="δv (mm)"
        aoClicar={(p) => setSel(maisPerto(p, pts, naTela(z.janela)))}>
        {({ px }) => (
          <>
            <Serie pontos={pts} px={px} />
            {a !== null && b !== null && (
              <>
                <Reta a={{ x: 0, y: a }} b={{ x: 1, y: a + b }} px={px} />
                {/* Taylor: a mesma reta com a abscissa 1,15 vez maior. */}
                <Reta a={{ x: 0, y: a }} b={{ x: 1.15, y: a + b }} px={px}
                  classe="reta auxiliar" />
              </>
            )}
            {yFim !== null && (
              <Reta a={{ x: 0, y: yFim }} b={{ x: 1, y: yFim }} px={px}
                classe="reta auxiliar" />
            )}
            {p90 && <Achado p={p90} px={px} rotulo="t₉₀" />}
            {p100 && <Achado p={p100} px={px} rotulo="t₁₀₀" />}
            {(['a', 'b', 'fim'] as const).map((k) => {
              const q = ponto(daOrdem(rasc[k]));
              return q && (
                <Marco key={k} p={q} px={px}
                  rotulo={k === 'a' ? 'A' : k === 'b' ? 'B' : 'fim do primário'} />
              );
            })}
            {lSel && (
              <g className="selecionada">
                <circle cx={px(ponto(lSel)!).x} cy={px(ponto(lSel)!).y} r={7} />
              </g>
            )}
          </>
        )}
      </Plano>
      <p className="legenda-grafico">
        Clique na curva para selecionar a leitura mais próxima e diga o que
        ela é: um dos dois pontos do trecho inicial (A e B) ou o fim do
        adensamento primário. A reta cheia passa por A e B; a tracejada
        inclinada é a de Taylor; a horizontal é o fim do primário.
      </p>
      {lSel && (
        <div className="leitura-selecionada" role="status">
          <span>Leitura selecionada: <b>{descreve(Number(lSel.ordem))}</b></span>
          <button type="button" className="botao secundario" onClick={marca('a')}>
            Ponto A da reta
          </button>
          <button type="button" className="botao secundario" onClick={marca('b')}>
            Ponto B da reta
          </button>
          <button type="button" className="botao secundario" onClick={marca('fim')}>
            Fim do primário
          </button>
          <button type="button" className="discreto" onClick={() => setSel(null)}>
            limpar
          </button>
        </div>
      )}
      <dl className="tracado-rascunho">
        <div><dt>A</dt><dd>{descreve(rasc.a)}</dd></div>
        <div><dt>B</dt><dd>{descreve(rasc.b)}</dd></div>
        <div><dt>Fim do primário</dt><dd>{descreve(rasc.fim)}</dd></div>
      </dl>
      <button type="button" className="primario"
        disabled={gravando || !mudou || rasc.a === null || rasc.b === null
          || rasc.a === rasc.b}
        onClick={() => aoGravar({
          pontoA: rasc.a as number, pontoB: rasc.b as number, pontoFim: rasc.fim,
        })}>
        {gravando ? 'Gravando…' : 'Gravar o traçado'}
      </button>
    </figure>
  );
}

// ══ O cisalhamento: τ × δh e δv × δh ══════════════════════════════════

/**
 * As curvas de TODOS os corpos de prova juntos — τ × δh e δv × δh, como
 * no laudo — e, do corpo de prova escolhido, a curva com o ponto que cada
 * critério escolhe. «Nominal» ou «corrigida» só troca a vista.
 */
export function GraficosDoCisalhamentoCd({ d, cps, cpId, base, aoApontar, apontando }: {
  d: EnsaioCisalhamentoDireto;
  cps: CorpoDeProvaCd[];
  cpId: string | null;
  base: 'nominal' | 'corrigida';
  aoApontar(deslocamentoMm: number): void;
  apontando: boolean;
}) {
  const [sel, setSel] = useState<number | null>(null);
  useEffect(() => { setSel(null); }, [cpId, base]);
  const tau = (l: any) => (base === 'nominal' ? l.tau_nominal_kpa : l.tau_corrigida_kpa);
  const vivos = cps.filter((c) => c.cp.situacao !== 'descartado');
  const curvas = vivos.map((c, k) => ({
    id: c.cp.id, nome: c.cp.identificacao, classe: classeCp(k),
    leituras: (c.cisalhamento ?? []).filter((l: any) => !l.descartada),
  }));
  const tauPts = curvas.map((c) => serie(c.leituras, (l) => l.deslocamento_horizontal_mm, tau));
  const dvPts = curvas.map((c) => serie(c.leituras,
    (l) => l.deslocamento_horizontal_mm, (l) => l.deslocamento_vertical_mm));
  const rupt = (d.rupturas ?? []).filter((r: any) => r.base === base
    && !r.motivo_sem_ruptura && r.situacao !== 'descartado');
  if (!tauPts.some((p) => p.length >= 2)) return null;

  const corrente = cps.find((c) => c.cp.id === cpId) ?? null;
  const lCorr = (corrente?.cisalhamento ?? []).filter((l: any) => !l.descartada
    && num(l.deslocamento_horizontal_mm) !== null && num(tau(l)) !== null);
  const ptsCorr = serie(lCorr, (l) => l.deslocamento_horizontal_mm, tau);
  const jCorr = janelaDoZero(ptsCorr);
  const porLeitura = new Map<number, { siglas: string[]; adotado: boolean; p: Ponto }>();
  for (const k of (corrente?.criterios ?? []).filter((x: any) => x.base === base)) {
    const o = num(k.ordem_leitura); const x = num(k.deslocamento_horizontal_mm);
    const y = num(k.tau_kpa);
    if (o === null || x === null || y === null) continue;
    const g = porLeitura.get(o) ?? { siglas: [], adotado: false, p: { x, y } };
    g.siglas.push(SIGLA_CRITERIO_CD[k.criterio] ?? k.criterio);
    g.adotado = g.adotado || !!k.adotado;
    porLeitura.set(o, g);
  }
  const lSel = sel !== null ? lCorr[sel] : null;

  return (
    <div className="graficos-triaxial" aria-label="Gráficos do cisalhamento">
      <div className="graficos-grade">
        <figure className="grafico grafico-triaxial" data-grafico="tau-dh">
          <figcaption>Tensão cisalhante × deslocamento horizontal — {base}</figcaption>
          <Plano janela={janelaDoZero(tauPts.flat())}
            tituloX="δh (mm)" tituloY={`τ ${base} (kPa)`}>
            {({ px }) => (
              <>
                {curvas.map((c, k) => (
                  <g key={c.id} className={c.classe}>
                    <Serie pontos={tauPts[k]} px={px} marcar={false} />
                  </g>
                ))}
                {rupt.map((r: any) => (
                  <Achado key={r.cp_id} px={px} rotulo={r.identificacao}
                    p={{ x: Number(r.deslocamento_horizontal_mm), y: Number(r.tau_kpa) }} />
                ))}
              </>
            )}
          </Plano>
        </figure>
        {dvPts.some((p) => p.length >= 2) && (
          <figure className="grafico grafico-triaxial" data-grafico="dv-dh">
            <figcaption>Deslocamento vertical × deslocamento horizontal</figcaption>
            <Plano janela={janelaDoZero(dvPts.flat())}
              tituloX="δh (mm)" tituloY="δv (mm)">
              {({ px }) => (
                <>
                  {curvas.map((c, k) => (
                    <g key={c.id} className={c.classe}>
                      <Serie pontos={dvPts[k]} px={px} marcar={false} />
                    </g>
                  ))}
                </>
              )}
            </Plano>
            <p className="legenda-grafico">δv negativo é compressão; positivo, dilatação.</p>
          </figure>
        )}
        {corrente && ptsCorr.length >= 2 && (
          <figure className="grafico grafico-triaxial" data-grafico="criterios">
            <figcaption>Os critérios na curva — {corrente.cp.identificacao}</figcaption>
            <Plano janela={jCorr} tituloX="δh (mm)" tituloY={`τ ${base} (kPa)`}
              aoClicar={(p) => setSel(maisPerto(p, ptsCorr, naTela(jCorr)))}>
              {({ px }) => (
                <>
                  <Serie pontos={ptsCorr} px={px} marcar={false} />
                  {[...porLeitura.entries()].filter(([, g]) => !g.adotado).map(([o, g]) => (
                    <Marco key={o} p={g.p} px={px} rotulo={g.siglas.join(' · ')} />
                  ))}
                  {[...porLeitura.entries()].filter(([, g]) => g.adotado).map(([o, g]) => (
                    <Achado key={o} p={g.p} px={px} rotulo={`ruptura · ${g.siglas.join(' · ')}`} />
                  ))}
                  {lSel && (
                    <g className="selecionada">
                      <circle cx={px(ptsCorr[sel as number]).x}
                        cy={px(ptsCorr[sel as number]).y} r={7} />
                    </g>
                  )}
                </>
              )}
            </Plano>
          </figure>
        )}
      </div>
      <ul className="legenda-cps">
        {curvas.map((c) => (
          <li key={c.id} className={c.classe.replace('trajetoria ', '')}>
            <svg viewBox="0 0 28 8" aria-hidden="true"><line x1="0" y1="4" x2="28" y2="4" /></svg>
            {c.nome}
          </li>
        ))}
      </ul>
      <p className="legenda-grafico">
        <span className="leg-achado">●</span> ruptura adotada ·{' '}
        <span className="leg-marco">○</span> ponto de outro critério · na
        curva do corpo de prova escolhido, clique para selecionar a leitura.
      </p>
      {lSel && (
        <div className="leitura-selecionada" role="status">
          <span>Leitura selecionada: <b>δh = {br(Number(lSel.deslocamento_horizontal_mm), 3)} mm</b></span>
          <span>δh/L {br(Number(lSel.deslocamento_relativo_pct), 2)} %</span>
          <span>τ {br(Number(tau(lSel)), 2)} kPa</span>
          <button type="button" className="botao secundario" disabled={apontando}
            onClick={() => aoApontar(Number(lSel.deslocamento_horizontal_mm))}>
            Apontar esta leitura como ruptura
          </button>
          <button type="button" className="discreto" onClick={() => setSel(null)}>
            limpar
          </button>
        </div>
      )}
    </div>
  );
}

// ══ A envoltória τ × σ ════════════════════════════════════════════════

/**
 * Os pontos de ruptura e a envoltória τ = c + σ·tg φ — nominal (cheia) e
 * corrigida (tracejada) — e, se pedidas, as retas de outros critérios
 * para comparar. O tg φ vem do banco: a tela não converte ângulo.
 */
export function GraficoDaEnvoltoriaCd({ d, comparar }: {
  d: EnsaioCisalhamentoDireto;
  comparar: { criterio: string; base: string }[];
}) {
  const rupt = (d.rupturas ?? []).filter((r: any) => !r.motivo_sem_ruptura
    && r.situacao !== 'descartado' && num(r.sigma_kpa) !== null && num(r.tau_kpa) !== null);
  if (!rupt.length) return null;
  const pts = rupt.map((r: any) => ({ x: Number(r.sigma_kpa), y: Number(r.tau_kpa) }));
  const j = janelaDoZero(pts, 0.12);
  const env = (base: string) => (d.envoltorias ?? []).find(
    (e: any) => e.base === base && !e.motivo_sem_envoltoria) ?? null;
  const nom = env('nominal'); const cor = env('corrigida');
  const extras = (d.envoltoriasCriterios ?? []).filter((e: any) => !e.motivo_sem_envoltoria
    && comparar.some((c) => c.criterio === e.criterio && c.base === e.base));
  const reta = (e: any) => ({ a: { x: 0, y: Number(e.coesao_kpa) },
    b: { x: 1, y: Number(e.coesao_kpa) + Number(e.tg_phi) } });
  return (
    <figure className="grafico grafico-triaxial" data-grafico="envoltoria">
      <figcaption>Envoltória de resistência τ × σ</figcaption>
      <Plano janela={j} tituloX="σ (kPa)" tituloY="τ (kPa)">
        {({ px }) => (
          <>
            {nom && <Reta {...reta(nom)} px={px} />}
            {cor && <Reta {...reta(cor)} px={px} classe="reta auxiliar" />}
            {extras.map((e: any) => (
              <Reta key={`${e.criterio}-${e.base}`} {...reta(e)} px={px} classe="reta auxiliar" />
            ))}
            {rupt.filter((r: any) => r.base === 'nominal').map((r: any) => (
              <Achado key={`n-${r.cp_id}`} px={px} rotulo={r.identificacao}
                p={{ x: Number(r.sigma_kpa), y: Number(r.tau_kpa) }} />
            ))}
            {rupt.filter((r: any) => r.base === 'corrigida').map((r: any) => (
              <Marco key={`c-${r.cp_id}`} px={px}
                p={{ x: Number(r.sigma_kpa), y: Number(r.tau_kpa) }} />
            ))}
          </>
        )}
      </Plano>
      <p className="legenda-grafico">
        <span className="leg-achado">●</span> ruptura nominal (ASTM D3080) ·{' '}
        <span className="leg-marco">○</span> ruptura corrigida pela área de contato
        {nom && <> · nominal: c = {br(Number(nom.coesao_kpa), 2)} kPa, φ = {br(Number(nom.phi_graus), 2)}°</>}
        {cor && <> · corrigida (tracejada): c = {br(Number(cor.coesao_kpa), 2)} kPa, φ = {br(Number(cor.phi_graus), 2)}°</>}
        {extras.length > 0 && ' · tracejadas também: ' + extras.map((e: any) =>
          `${SIGLA_CRITERIO_CD[e.criterio] ?? e.criterio} (${e.base})`).join(', ')}
      </p>
    </figure>
  );
}
